import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SPECIALITE_LABELS = {
  fauteuil_dentaire:"Fauteuil dentaire", radiologie:"Radiologie",
  turbine_contre_angle:"Turbines & Contre-angles", sterilisation_autoclave:"Sterilisation & Autoclave",
  compresseur:"Compresseur", aspiration:"Aspiration", unite_dentaire:"Unite dentaire",
  scanner_cao_cam:"Scanner & CAO/CAM", electronique_generale:"Electronique generale",
  plomberie_circuits_eau:"Plomberie & Circuits eau", autre:"Autre",
};

const WILAYAS = ["01 - Adrar","02 - Chlef","03 - Laghouat","04 - Oum El Bouaghi","05 - Batna","06 - Béjaïa","07 - Biskra","08 - Béchar","09 - Blida","10 - Bouira","11 - Tamanrasset","12 - Tébessa","13 - Tlemcen","14 - Tiaret","15 - Tizi Ouzou","16 - Alger","17 - Djelfa","18 - Jijel","19 - Sétif","20 - Saïda","21 - Skikda","22 - Sidi Bel Abbès","23 - Annaba","24 - Guelma","25 - Constantine","26 - Médéa","27 - Mostaganem","28 - M'Sila","29 - Mascara","30 - Ouargla","31 - Oran","32 - El Bayadh","33 - Illizi","34 - Bordj Bou Arréridj","35 - Boumerdès","36 - El Tarf","37 - Tindouf","38 - Tissemsilt","39 - El Oued","40 - Khenchela","41 - Souk Ahras","42 - Tipaza","43 - Mila","44 - Aïn Defla","45 - Naâma","46 - Aïn Témouchent","47 - Ghardaïa","48 - Relizane","49 - Timimoun","50 - Bordj Badji Mokhtar","51 - Ouled Djellal","52 - Béni Abbès","53 - In Salah","54 - In Guezzam","55 - Touggourt","56 - Djanet","57 - El M'Ghair","58 - El Meniaa","59 - Aflou","60 - Barika","61 - Ksar Chellala","62 - Messaâd","63 - Aïn Oussera","64 - Bou Saâda","65 - El Abiodh Sidi Cheikh","66 - El Kantara","67 - Bir El Ater","68 - Ksar El Boukhari","69 - El Aricha"];

const Stars = ({ rating }) => (
  <span className="text-sm">
    {[1,2,3,4,5].map(i => <span key={i} className={i <= Math.round(rating) ? "text-[#E0A52E]" : "text-gray-300"}>★</span>)}
  </span>
);

const RepCard = ({ rep, onClick }) => (
  <div onClick={() => onClick(rep)} className="bg-white border border-[#E8E0D0] rounded-2xl p-5 cursor-pointer hover:border-[#1F4D3F] hover:shadow-md transition-all">
    <div className="flex items-start gap-4 mb-3">
      <div className="w-14 h-14 rounded-2xl bg-[#1F4D3F] flex items-center justify-center text-white text-xl font-bold shrink-0">{(rep.name||"?")[0].toUpperCase()}</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#2D2A26] truncate">{rep.name}</div>
        <div className="flex items-center gap-2 mt-1"><Stars rating={rep.rating||0} /><span className="text-xs text-[#9A8F82]">({rep.reviews_count||0} avis)</span></div>
        <div className="text-xs text-[#7A6F5C] mt-1">🏆 {rep.missions_count||0} missions</div>
      </div>
      {rep.disponible && <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full shrink-0">Disponible</span>}
    </div>
    <div className="flex flex-wrap gap-1 mb-3">
      {(rep.specialites||[]).slice(0,3).map(s => <span key={s} className="text-xs bg-[#E4EDE9] text-[#1F4D3F] px-2 py-1 rounded-full">{SPECIALITE_LABELS[s]||s}</span>)}
      {(rep.specialites||[]).length > 3 && <span className="text-xs text-[#9A8F82] px-1">+{rep.specialites.length-3}</span>}
    </div>
    <div className="text-xs text-[#9A8F82]">📍 {(rep.wilayas_couvertes||[]).slice(0,2).join(" · ")}{(rep.wilayas_couvertes||[]).length > 2 ? ` +${rep.wilayas_couvertes.length-2}` : ""}</div>
    {rep.tarif_deplacement && <div className="text-xs text-[#C75B27] mt-2 font-medium">Deplacement : {Math.round(rep.tarif_deplacement).toLocaleString("fr")} DA</div>}
  </div>
);

const RepModal = ({ rep, onClose, onReviewed }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [savingReview, setSavingReview] = useState(false);

  const submitReview = async () => {
    if (!user) { navigate("/connexion"); return; }
    setSavingReview(true);
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/reparateurs/" + rep.id + "/reviews", {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ rating: stars, comment }),
      });
      if (r.ok) {
        toast({ title: "✅ Merci pour votre avis !" });
        setShowReview(false); setComment(""); setStars(5);
        onReviewed?.();
        onClose();
      } else {
        const d = await r.json();
        toast({ title: "Erreur", description: d.detail || "Erreur", variant: "destructive" });
      }
    } catch {}
    setSavingReview(false);
  };

  const contact = async () => {
    if (!msg.trim()) return;
    if (!user) { navigate("/connexion"); return; }
    setSending(true);
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/appels-offres", {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({ title:"Contact direct - "+rep.name, description:msg, wilaya:rep.wilayas_couvertes?.[0]||"", type_panne:"autre" }),
      });
      if (r.ok) { toast({ title:"Message envoye !", description:rep.name+" sera notifie" }); setMsg(""); onClose(); }
    } catch {}
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="bg-[#1F4D3F] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-white font-semibold">{rep.name}</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl">x</button>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4"><Stars rating={rep.rating||0} /><span className="text-[#9A8F82] text-sm">({rep.reviews_count||0} avis · {rep.missions_count||0} missions)</span></div>
          {rep.description && <p className="text-sm text-[#5A5247] bg-[#F5F1EA] rounded-xl p-4 mb-4">{rep.description}</p>}
          <div className="mb-4">
            <h3 className="font-medium text-sm text-[#2D2A26] mb-2">Specialites</h3>
            <div className="flex flex-wrap gap-2">{(rep.specialites||[]).map(s => <span key={s} className="text-xs bg-[#E4EDE9] text-[#1F4D3F] px-3 py-1 rounded-full">{SPECIALITE_LABELS[s]||s}</span>)}</div>
          </div>
          <div className="mb-4">
            <h3 className="font-medium text-sm text-[#2D2A26] mb-2">Wilayas couvertes</h3>
            <div className="flex flex-wrap gap-1">{(rep.wilayas_couvertes||[]).map(w => <span key={w} className="text-xs bg-[#F5F1EA] text-[#7A6F5C] px-2 py-1 rounded-full">{w}</span>)}</div>
          </div>
          {(rep.tarif_deplacement || rep.tarif_horaire) && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              {rep.tarif_deplacement && <div className="bg-[#F5F1EA] rounded-xl p-3 text-center"><div className="text-lg font-bold text-[#1F4D3F]">{Math.round(rep.tarif_deplacement).toLocaleString("fr")} DA</div><div className="text-xs text-[#9A8F82]">Deplacement</div></div>}
              {rep.tarif_horaire && <div className="bg-[#F5F1EA] rounded-xl p-3 text-center"><div className="text-lg font-bold text-[#1F4D3F]">{Math.round(rep.tarif_horaire).toLocaleString("fr")} DA</div><div className="text-xs text-[#9A8F82]">Tarif horaire</div></div>}
            </div>
          )}
          {(rep.reviews||[]).length > 0 && (
            <div className="mb-4">
              <h3 className="font-medium text-sm text-[#2D2A26] mb-2">Derniers avis</h3>
              {(rep.reviews||[]).slice(0,3).map(r => (
                <div key={r.id} className="border-l-2 border-[#E4EDE9] pl-3 mb-3">
                  <Stars rating={r.rating} /><p className="text-xs text-[#5A5247] mt-1">{r.comment}</p><div className="text-xs text-[#9A8F82] mt-1">{r.dentiste_name}</div>
                </div>
              ))}
            </div>
          )}
          <div className="border-t border-[#E8E0D0] pt-4">
            <h3 className="font-medium text-sm text-[#2D2A26] mb-2">Contacter ce reparateur</h3>
            <textarea value={msg} onChange={e => setMsg(e.target.value)} rows={3} placeholder="Decrivez votre panne..." className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none resize-none mb-3" />
            <button onClick={contact} disabled={!msg.trim()||sending} className="w-full bg-[#1F4D3F] text-white py-3 rounded-xl font-semibold text-sm disabled:opacity-50">{sending ? "Envoi..." : "Envoyer un message"}</button>
          </div>

          <div className="border-t border-[#E8E0D0] pt-4 mt-4">
            {!showReview ? (
              <button onClick={() => user ? setShowReview(true) : navigate("/connexion")} className="w-full border border-[#E0A52E] text-[#B8860B] hover:bg-[#FFF8E8] py-2.5 rounded-xl font-medium text-sm">
                ⭐ Noter ce réparateur
              </button>
            ) : (
              <div>
                <h3 className="font-medium text-sm text-[#2D2A26] mb-2">Votre note</h3>
                <div className="flex gap-1 mb-3 justify-center">
                  {[1,2,3,4,5].map(i => (
                    <button key={i} onClick={() => setStars(i)} className="text-3xl transition-transform hover:scale-110">
                      <span className={i <= stars ? "text-[#E0A52E]" : "text-gray-300"}>★</span>
                    </button>
                  ))}
                </div>
                <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} placeholder="Votre commentaire (optionnel)..." className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none resize-none mb-3" />
                <div className="flex gap-2">
                  <button onClick={submitReview} disabled={savingReview} className="flex-1 bg-[#E0A52E] hover:bg-[#c8941f] text-white py-2.5 rounded-xl font-semibold text-sm disabled:opacity-50">{savingReview ? "Envoi..." : "Publier mon avis"}</button>
                  <button onClick={() => setShowReview(false)} className="px-4 border border-[#E8E0D0] rounded-xl text-sm">Annuler</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const RegisterRepForm = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ name:user?.name||"", phone:"", description:"", specialites:[], wilayas_couvertes:[], tarif_deplacement:"", tarif_horaire:"" });
  const [saving, setSaving] = useState(false);
  const toggleSpec = (s) => setForm(f => ({...f, specialites: f.specialites.includes(s) ? f.specialites.filter(x=>x!==s) : [...f.specialites,s]}));
  const toggleWilaya = (w) => setForm(f => ({...f, wilayas_couvertes: f.wilayas_couvertes.includes(w) ? f.wilayas_couvertes.filter(x=>x!==w) : [...f.wilayas_couvertes,w]}));

  const submit = async () => {
    if (!form.name || form.specialites.length===0 || form.wilayas_couvertes.length===0) { toast({title:"Erreur",description:"Nom, specialites et wilayas obligatoires",variant:"destructive"}); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("dz_token");
      const r = await fetch(API + "/api/reparateurs/register", {
        method:"POST", headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({...form, tarif_deplacement:form.tarif_deplacement?parseFloat(form.tarif_deplacement):null, tarif_horaire:form.tarif_horaire?parseFloat(form.tarif_horaire):null}),
      });
      const d = await r.json();
      if (r.ok) { toast({title:"Inscription soumise !",description:"Approuvee sous 24h"}); onSuccess?.(); onClose(); }
      else toast({title:"Erreur",description:d.detail||"Erreur",variant:"destructive"});
    } catch {}
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="bg-[#C75B27] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-white font-semibold text-lg">Devenir reparateur</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl">x</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium block mb-1">Nom *</label><input value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none" /></div>
            <div><label className="text-sm font-medium block mb-1">Telephone *</label><input value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))} placeholder="0555 00 00 00" className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none" /></div>
          </div>
          <div><label className="text-sm font-medium block mb-1">Description</label><textarea value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} rows={2} className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm focus:outline-none resize-none" /></div>
          <div>
            <label className="text-sm font-medium block mb-2">Specialites * (plusieurs)</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(SPECIALITE_LABELS).map(([k,v]) => (
                <button key={k} onClick={() => toggleSpec(k)} className={"px-3 py-2 rounded-xl border text-xs font-medium text-left " + (form.specialites.includes(k) ? "bg-[#C75B27] text-white border-[#C75B27]" : "bg-white border-[#E8E0D0] text-[#5A5247]")}>{v}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Wilayas couvertes * (plusieurs)</label>
            <div className="max-h-40 overflow-y-auto border border-[#E8E0D0] rounded-xl p-2 grid grid-cols-2 gap-1">
              {WILAYAS.map(w => <button key={w} onClick={() => toggleWilaya(w)} className={"px-2 py-1.5 rounded-lg text-xs font-medium text-left " + (form.wilayas_couvertes.includes(w) ? "bg-[#1F4D3F] text-white" : "bg-white text-[#5A5247] hover:bg-[#F5F1EA]")}>{w}</button>)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium block mb-1">Tarif deplacement (DA)</label><input value={form.tarif_deplacement} onChange={e => setForm(f=>({...f,tarif_deplacement:e.target.value}))} type="number" className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm" /></div>
            <div><label className="text-sm font-medium block mb-1">Tarif horaire (DA)</label><input value={form.tarif_horaire} onChange={e => setForm(f=>({...f,tarif_horaire:e.target.value}))} type="number" className="w-full px-3 py-2.5 border border-[#E8E0D0] rounded-xl text-sm" /></div>
          </div>
          <button onClick={submit} disabled={saving} className="w-full bg-[#C75B27] text-white py-3 rounded-xl font-semibold text-sm disabled:opacity-50">{saving ? "Envoi..." : "Soumettre mon inscription"}</button>
        </div>
      </div>
    </div>
  );
};

const Reparateurs = () => {
  useDocumentMeta({
    title: "Réparateurs matériel dentaire en Algérie — Techniciens agréés | Comptoir Online",
    description: "Trouvez un technicien qualifié pour réparer votre matériel dentaire en Algérie. Réparation fauteuil dentaire, radiologie, turbine, autoclave. Intervention rapide, devis gratuit, techniciens notés.",
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [filterSpec, setFilterSpec] = useState("");
  const [filterWilaya, setFilterWilaya] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      let url = API + "/api/reparateurs?limit=50";
      if (filterSpec) url += "&specialite=" + filterSpec;
      if (filterWilaya) url += "&wilaya=" + encodeURIComponent(filterWilaya);
      if (search) url += "&q=" + encodeURIComponent(search);
      const r = await fetch(url);
      const d = await r.json();
      setReps(Array.isArray(d) ? d : []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterSpec, filterWilaya, search]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#1F4D3F] mb-1">Reparateurs agrees</h1>
          <p className="text-[#7A6F5C] text-sm">Trouvez un technicien qualifie pour reparer votre materiel dentaire</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => user ? setShowRegister(true) : navigate("/connexion")} className="border border-[#C75B27] text-[#C75B27] hover:bg-[#FFF3E0] px-5 py-2.5 rounded-xl font-medium text-sm">Devenir reparateur</button>
          <button onClick={() => user ? navigate("/appels-offres/nouveau") : navigate("/connexion")} className="bg-[#C75B27] text-white hover:bg-[#a84d21] px-5 py-2.5 rounded-xl font-medium text-sm">+ Lancer un appel d'offre</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mb-6">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="px-4 py-2 border border-[#E8E0D0] rounded-full text-sm focus:outline-none min-w-[180px]" />
        <select value={filterSpec} onChange={e => setFilterSpec(e.target.value)} className="px-4 py-2 border border-[#E8E0D0] rounded-full text-sm bg-white focus:outline-none">
          <option value="">Toutes specialites</option>
          {Object.entries(SPECIALITE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filterWilaya} onChange={e => setFilterWilaya(e.target.value)} className="px-4 py-2 border border-[#E8E0D0] rounded-full text-sm bg-white focus:outline-none">
          <option value="">Toutes les wilayas</option>
          {WILAYAS.map(w => <option key={w} value={w}>{w}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({length:6}).map((_,i) => <div key={i} className="h-52 bg-[#F5F1EA] rounded-2xl animate-pulse" />)}</div>
      ) : reps.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🔧</div>
          <h2 className="text-xl font-semibold text-[#1F4D3F] mb-2">Aucun reparateur pour l'instant</h2>
          <button onClick={() => user ? setShowRegister(true) : navigate("/connexion")} className="bg-[#C75B27] text-white px-6 py-3 rounded-xl font-semibold text-sm mt-4">Devenir reparateur</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{reps.map(r => <RepCard key={r.id} rep={r} onClick={setSelected} />)}</div>
      )}
      {selected && <RepModal rep={selected} onClose={() => setSelected(null)} onReviewed={load} />}
      {showRegister && <RegisterRepForm onClose={() => setShowRegister(false)} onSuccess={load} />}
    </div>
  );
};

export default Reparateurs;
